import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Mail, Phone, Menu, X, Instagram, Play, Sparkles, Compass } from "lucide-react";
import { BRAND, VERTICALS, CLIENTS } from "@/data/site";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.8, delay, ease: [0.2, 0.8, 0.2, 1] },
});

/* ---------------- NAVBAR ---------------- */
const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#nosotros", label: "Nosotros" },
    { href: "#verticales", label: "Verticales" },
    { href: "#casos", label: "Casos" },
    { href: "#clientes", label: "Clientes" },
    { href: "#contacto", label: "Contacto" },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#03123F]/85 backdrop-blur-xl border-b border-white/5" : ""
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between py-4">
        <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={BRAND.logoMonoLime} alt="Néctar" className="h-9 w-9" />
          <div className="leading-none">
            <div className="font-display text-[1.1rem] tracking-tight" style={{ color: "#f6f3ec" }}>
              Néctar<span style={{ color: "var(--lime)" }}>.</span>
            </div>
            <div className="text-[0.62rem] tracking-[0.32em] uppercase opacity-60">Negocios</div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="link-underline text-sm font-medium opacity-90 hover:opacity-100"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a href="#contacto" className="pill pill-lime text-sm" data-testid="nav-cta">
            Hablemos <ArrowUpRight size={16} />
          </a>
        </div>

        <button
          className="md:hidden text-paper p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          style={{ color: "var(--paper)" }}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#050a23] border-t border-white/10" data-testid="nav-mobile-menu">
          <div className="flex flex-col p-6 gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-lg font-display"
                style={{ color: "var(--paper)" }}
              >
                {l.label}
              </a>
            ))}
            <a href="#contacto" onClick={() => setOpen(false)} className="pill pill-lime w-fit text-sm">
              Hablemos <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

/* ---------------- HERO ---------------- */
const Hero = () => {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 600], [0, -80]);
  const y2 = useTransform(scrollY, [0, 600], [0, 100]);

  return (
    <section id="top" data-testid="hero" className="relative min-h-[100svh] flex items-center overflow-hidden pt-28">
      {/* halos */}
      <div className="halo" style={{ background: "var(--purple)", width: 600, height: 600, top: -150, left: -200 }} />
      <div className="halo" style={{ background: "var(--lime)", width: 500, height: 500, bottom: -200, right: -200, opacity: 0.18 }} />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 w-full relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-9">
            <motion.div {...fade(0)} className="flex items-center gap-3 mb-8">
              <span className="kicker">Agencia estratégica · MX</span>
              <span className="h-px w-16" style={{ background: "rgba(246,243,236,0.3)" }} />
              <span className="text-[0.7rem] opacity-70 tracking-widest">EST. NÉCTAR</span>
            </motion.div>

            <motion.h1 {...fade(0.1)} className="hero-type text-[clamp(3rem,9.5vw,9.5rem)]">
              <span style={{ color: "var(--paper)" }}>Descubrimos</span>
              <br />
              <span className="hero-italic" style={{ color: "var(--lime)" }}>el corazón</span>
              <br />
              <span style={{ color: "var(--paper)" }}>de tu marca.</span>
            </motion.h1>

            <motion.p {...fade(0.25)} className="mt-10 max-w-2xl text-lg md:text-xl opacity-80 leading-relaxed">
              Extraemos la esencia única de las marcas para crear ideas estratégicas que conectan, se sienten y se recuerdan.
              En aeronáutica, <em className="font-display italic" style={{ color: "var(--lime)" }}>el Néctar</em> simboliza el norte —y eso es lo que damos: rumbo claro.
            </motion.p>

            <motion.div {...fade(0.4)} className="mt-12 flex flex-wrap gap-4 items-center">
              <a href="#contacto" className="pill pill-lime" data-testid="hero-cta-primary">
                ¿Hablamos del siguiente paso? <ArrowUpRight size={18} />
              </a>
              <a href="#verticales" className="pill pill-ghost" data-testid="hero-cta-secondary">
                Ver verticales
              </a>
            </motion.div>
          </div>

          <motion.div {...fade(0.5)} className="lg:col-span-3 flex lg:justify-end items-center gap-6 mt-20 lg:mt-0 order-last">
            <div className="flex flex-col items-start lg:items-end">
              <div className="text-[0.65rem] tracking-[0.32em] uppercase opacity-60 mb-2">Fundador</div>
              <div className="font-display text-2xl" style={{ color: "var(--paper)" }}>{BRAND.founder}</div>
              <div className="text-xs opacity-60 mt-1">Nuevos Negocios</div>
            </div>
            <motion.img
              style={{ y: y2 }}
              src={BRAND.logoMonoPurple}
              alt="N isotipo"
              className="float-slow w-20 h-20 lg:w-24 lg:h-24 opacity-90"
            />
          </motion.div>
        </div>

        {/* bottom marquee */}
        <motion.div {...fade(0.7)} className="mt-24 lg:mt-32 border-t border-white/10 pt-6 overflow-hidden">
          <div className="marquee-track gap-12 text-2xl md:text-3xl font-display italic opacity-70">
            {Array.from({ length: 2 }).flatMap((_, k) =>
              ["Estrategia", "Content & 3D", "Eventos", "Publicidad Aérea", "BTL Inflable", "Mkt Digital", "Consultoría", "Néctar"].map((w, i) => (
                <span key={`${k}-${i}`} className="flex items-center gap-12">
                  {w}
                  <span style={{ color: "var(--lime)" }}>✦</span>
                </span>
              ))
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- ABOUT / CONCEPTO ---------------- */
const About = () => (
  <section id="nosotros" data-testid="about" className="py-28 lg:py-40 relative overflow-hidden">
    <div className="halo" style={{ background: "var(--purple)", width: 700, height: 700, top: "20%", right: "-30%", opacity: 0.22 }} />
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
      <div className="grid lg:grid-cols-12 gap-12">
        <motion.div {...fade(0)} className="lg:col-span-4">
          <div className="kicker mb-6">[ 01 — Concepto ]</div>
          <h2 className="font-display text-5xl md:text-6xl leading-[0.95]" style={{ color: "var(--paper)" }}>
            Buscamos siempre dar un <span className="hero-italic" style={{ color: "var(--lime)" }}>rumbo claro</span> a las marcas.
          </h2>
        </motion.div>

        <motion.div {...fade(0.15)} className="lg:col-span-7 lg:col-start-6 space-y-8">
          <p className="text-xl md:text-2xl leading-relaxed opacity-90 font-display font-light">
            Extraemos la esencia de las marcas — aquello que las hace únicas y valiosas — y con ese
            <span style={{ color: "var(--lime)" }}> Néctar </span>
            creamos ideas que <em className="italic">se ven, se sienten y saben</em> a ese mismo Néctar, para conectar profundamente con las audiencias.
          </p>

          <div className="glass rounded-2xl p-7 flex items-start gap-5">
            <Compass size={28} style={{ color: "var(--lime)" }} className="shrink-0 mt-1" />
            <p className="text-base md:text-lg opacity-80">
              Coincidentemente, en aeronáutica <span className="font-display italic" style={{ color: "var(--paper)" }}>“El Néctar”</span> simboliza el norte.
              Porque de igual manera, buscamos siempre dar un rumbo claro a las marcas. Nuestro enfoque es enteramente estratégico.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-6 pt-6 border-t border-white/10">
            {[
              { n: "+15", t: "Años de oficio" },
              { n: "6", t: "Verticales" },
              { n: "100%", t: "Estratégico" },
            ].map((s, i) => (
              <div key={i}>
                <div className="font-numerals text-4xl md:text-5xl" style={{ color: "var(--lime)" }}>{s.n}</div>
                <div className="text-xs uppercase tracking-[0.25em] opacity-60 mt-2">{s.t}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ---------------- VERTICALES OVERVIEW ---------------- */
const Verticales = () => (
  <section id="verticales" data-testid="verticales" className="py-28 lg:py-36 relative">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <motion.div {...fade(0)} className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
        <div>
          <div className="kicker mb-4">[ 02 — Verticales ]</div>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.9]" style={{ color: "var(--paper)" }}>
            Seis caminos.<br />
            <span className="hero-italic" style={{ color: "var(--lime)" }}>Un solo norte.</span>
          </h2>
        </div>
        <p className="max-w-md opacity-70 text-lg">
          Cada vertical es una herramienta para extraer y comunicar el Néctar de tu marca. Las combinamos en función de tu estrategia.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {VERTICALS.map((v, i) => (
          <motion.a
            key={v.id}
            href={`#${v.id}`}
            data-testid={`vertical-card-${v.id}`}
            {...fade(i * 0.06)}
            className="group relative p-7 lg:p-8 rounded-2xl glass overflow-hidden hover:bg-white/[0.07] transition-all duration-500"
            style={{ minHeight: 280 }}
          >
            <div className="flex items-start justify-between mb-10">
              <span
                className="font-numerals text-2xl"
                style={{ color: "var(--lime)" }}
              >
                {v.number}
              </span>
              <ArrowUpRight
                size={22}
                className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                style={{ color: "var(--lime)" }}
              />
            </div>
            <h3 className="font-display text-3xl mb-4 leading-tight" style={{ color: "var(--paper)" }}>
              {v.name}
            </h3>
            <p className="text-sm opacity-70 leading-relaxed">{v.short}</p>

            <div
              className="absolute -bottom-24 -right-24 w-56 h-56 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{ background: "var(--purple)", filter: "blur(80px)" }}
            />
          </motion.a>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- CASE EMBED COMPONENTS ---------------- */
const YouTubeCard = ({ c }) => (
  <div className="space-y-3">
    <div className="yt-wrap">
      <iframe
        src={c.embed}
        title={c.title}
        loading="lazy"
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="font-display text-xl" style={{ color: "var(--paper)" }}>{c.title}</div>
        {c.subtitle && <div className="text-xs opacity-60 mt-1">{c.subtitle}</div>}
      </div>
      <a
        href={c.url}
        target="_blank"
        rel="noreferrer"
        className="text-xs flex items-center gap-1 opacity-70 hover:opacity-100 shrink-0"
        style={{ color: "var(--lime)" }}
        data-testid={`case-link-${c.title}`}
      >
        Ver en YouTube <ArrowUpRight size={14} />
      </a>
    </div>
  </div>
);

const InstagramCard = ({ c }) => (
  <a
    href={c.url}
    target="_blank"
    rel="noreferrer"
    data-testid={`ig-${c.title}`}
    className="group block"
  >
    <div className="relative ig-aspect rounded-2xl overflow-hidden border border-white/10">
      {/* Background image or gradient */}
      {c.image ? (
        <>
          <img
            src={c.image}
            alt={c.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            style={{ objectPosition: c.imagePosition || "center" }}
          />
          {/* Dark gradient overlay for legibility */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(3,18,63,0.55) 0%, rgba(3,18,63,0.15) 40%, rgba(3,18,63,0.85) 100%)",
            }}
          />
        </>
      ) : (
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(113,53,229,0.75) 0%, rgba(3,18,63,0.95) 70%), radial-gradient(circle at 30% 20%, rgba(198,255,52,0.4), transparent 60%)",
          }}
        />
      )}

      <div className="absolute inset-0 flex flex-col justify-between p-5 z-10">
        <div className="flex items-center justify-between">
          <Instagram size={20} style={{ color: "var(--paper)" }} />
          <span
            className="text-[0.65rem] tracking-[0.25em] uppercase px-2 py-1 rounded-full"
            style={{ background: "rgba(3,18,63,0.6)", color: "var(--paper)" }}
          >
            {c.handle || "Instagram"}
          </span>
        </div>
        <div>
          <div className="font-display text-2xl md:text-3xl leading-tight drop-shadow-lg" style={{ color: "var(--paper)" }}>
            {c.title}
          </div>
          {c.subtitle && (
            <div className="text-xs opacity-90 mt-2 drop-shadow">{c.subtitle}</div>
          )}
          <div
            className="mt-4 inline-flex items-center gap-2 text-xs font-semibold tracking-wide"
            style={{ color: "var(--lime)" }}
          >
            <Play size={14} fill="currentColor" />
            Ver en Instagram
          </div>
        </div>
      </div>
    </div>
  </a>
);

const ImagePlaceholder = ({ label, ratio = "aspect-[4/3]" }) => (
  <div
    className={`relative ${ratio} rounded-2xl overflow-hidden border border-dashed border-white/15 flex items-center justify-center`}
    style={{
      background:
        "linear-gradient(135deg, rgba(113,53,229,0.18), rgba(3,18,63,0.4)), radial-gradient(circle at 20% 20%, rgba(198,255,52,0.12), transparent 60%)",
    }}
  >
    <div className="text-center px-6">
      <div className="text-[0.65rem] tracking-[0.32em] uppercase opacity-60 mb-2">Placeholder</div>
      <div className="font-display text-lg opacity-80" style={{ color: "var(--paper)" }}>{label}</div>
      <div className="text-[0.6rem] opacity-50 mt-2">Sustituir por imagen real</div>
    </div>
  </div>
);

/* ---------------- VERTICAL SECTIONS (DETAIL) ---------------- */
const VerticalSection = ({ v, index }) => {
  const isAlt = index % 2 === 1;
  return (
    <section
      id={v.id}
      data-testid={`vertical-section-${v.id}`}
      className="py-24 lg:py-32 relative overflow-hidden border-t border-white/5"
      style={{ background: isAlt ? "var(--ink-2)" : "transparent" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <motion.div {...fade(0)} className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <span className="font-numerals text-7xl" style={{ color: "var(--lime)" }}>{v.number}</span>
              <span className="kicker">{`Vertical · ${v.name}`}</span>
            </div>
            <h2 className="font-display text-5xl md:text-6xl leading-[0.95]" style={{ color: "var(--paper)" }}>
              {v.name}
            </h2>
          </motion.div>
          <motion.div {...fade(0.1)} className="lg:col-span-6 lg:col-start-7">
            <p className="text-lg md:text-xl opacity-85 leading-relaxed">{v.long}</p>
          </motion.div>
        </div>

        {/* Special: Consultoría pillars */}
        {v.id === "consultoria" && v.pillars && (
          <div className="grid md:grid-cols-3 gap-5 mb-10">
            {v.pillars.map((p, i) => (
              <motion.div {...fade(i * 0.1)} key={p.t} className="glass rounded-2xl p-7">
                <Sparkles size={22} style={{ color: "var(--lime)" }} />
                <h4 className="font-display text-2xl mt-4 mb-3" style={{ color: "var(--paper)" }}>{p.t}</h4>
                <p className="text-sm opacity-70 leading-relaxed">{p.d}</p>
              </motion.div>
            ))}
          </div>
        )}

        {/* Cases grid */}
        {v.cases && v.cases.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center justify-between mb-8">
              <div className="kicker">Casos de éxito</div>
              <div className="h-px flex-1 mx-6" style={{ background: "rgba(246,243,236,0.1)" }} />
              <div className="text-xs opacity-50">{v.cases.length} {v.cases.length === 1 ? "caso" : "casos"}</div>
            </div>

            {/* YouTube cases */}
            {v.cases.filter((c) => c.type === "youtube").length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {v.cases.filter((c) => c.type === "youtube").map((c) => (
                  <motion.div key={c.title} {...fade(0)}>
                    <YouTubeCard c={c} />
                  </motion.div>
                ))}
              </div>
            )}

            {/* Instagram cases */}
            {v.cases.filter((c) => c.type === "instagram").length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {v.cases.filter((c) => c.type === "instagram").map((c) => (
                  <motion.div key={c.title} {...fade(0)}>
                    <InstagramCard c={c} />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Placeholders for verticals without media */}
        {v.id === "btl-inflable" && (
          <div className="grid md:grid-cols-3 gap-5">
            {["Metas inflables", "Esferas a la medida", "Cabinas y juegos"].map((l, i) => (
              <motion.div key={l} {...fade(i * 0.1)}>
                <ImagePlaceholder label={l} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/* ---------------- CLIENTES ---------------- */
const Clientes = () => (
  <section id="clientes" data-testid="clientes" className="py-28 lg:py-36 relative overflow-hidden">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <motion.div {...fade(0)} className="mb-16 text-center">
        <div className="kicker mb-4">[ 03 — Clientes ]</div>
        <h2 className="font-display text-5xl md:text-7xl leading-[0.95]" style={{ color: "var(--paper)" }}>
          Marcas que ya<br />
          <span className="hero-italic" style={{ color: "var(--lime)" }}>encontraron su Néctar.</span>
        </h2>
      </motion.div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {CLIENTS.map((c, i) => (
          <motion.div
            key={c.name}
            {...fade(i * 0.04)}
            data-testid={`client-${i}`}
            className="aspect-[3/2] rounded-xl border border-white/10 flex flex-col items-center justify-center text-center px-4 hover:border-white/30 transition-all group overflow-hidden"
            style={{ background: "rgba(255,255,255,0.02)" }}
          >
            {c.logo ? (
              <img
                src={c.logo}
                alt={`Logo ${c.name}`}
                loading="lazy"
                className="max-h-[55%] max-w-[78%] object-contain opacity-80 group-hover:opacity-100 transition-opacity duration-500"
              />
            ) : (
              <>
                <div className="text-[0.55rem] tracking-[0.3em] uppercase opacity-40 mb-2">Logo</div>
                <div
                  className="font-display text-sm md:text-base opacity-90 group-hover:opacity-100"
                  style={{ color: "var(--paper)" }}
                >
                  {c.name}
                </div>
              </>
            )}
          </motion.div>
        ))}
      </div>

      <motion.div {...fade(0.4)} className="mt-12 text-center text-xs opacity-50">
        Algunas de las marcas que confían en nuestro proceso.
      </motion.div>
    </div>
  </section>
);

/* ---------------- CONTACTO ---------------- */
const Contacto = () => (
  <section
    id="contacto"
    data-testid="contacto"
    className="py-28 lg:py-40 relative overflow-hidden border-t border-white/5"
    style={{ background: "var(--ink-2)" }}
  >
    <div className="halo" style={{ background: "var(--lime)", width: 600, height: 600, top: "-30%", left: "30%", opacity: 0.15 }} />
    <div className="halo" style={{ background: "var(--purple)", width: 700, height: 700, bottom: "-40%", right: "-10%", opacity: 0.35 }} />

    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <motion.div {...fade(0)} className="lg:col-span-7">
          <div className="kicker mb-6">[ 04 — Contacto ]</div>
          <h2 className="hero-type text-[clamp(2.6rem,7vw,7rem)]" style={{ color: "var(--paper)" }}>
            ¿Hablamos del
            <br />
            <span className="hero-italic" style={{ color: "var(--lime)" }}>siguiente paso?</span>
          </h2>
          <p className="mt-8 text-xl opacity-80 max-w-xl">
            Estamos listos para extraer el verdadero néctar de tu marca.
          </p>
        </motion.div>

        <motion.div {...fade(0.15)} className="lg:col-span-5">
          <div className="glass rounded-3xl p-8 lg:p-10">
            <div className="text-xs tracking-[0.32em] uppercase opacity-60 mb-2">Fundador</div>
            <div className="font-display text-3xl mb-8" style={{ color: "var(--paper)" }}>{BRAND.founder}</div>

            <a
              href={`mailto:${BRAND.email}`}
              data-testid="contact-email"
              className="flex items-center gap-4 py-5 border-t border-white/10 group"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--purple)" }}>
                <Mail size={18} style={{ color: "var(--paper)" }} />
              </div>
              <div className="flex-1">
                <div className="text-[0.65rem] tracking-[0.25em] uppercase opacity-60">Email</div>
                <div className="font-medium group-hover:text-[var(--lime)] transition-colors" style={{ color: "var(--paper)" }}>
                  {BRAND.email}
                </div>
              </div>
              <ArrowUpRight size={20} className="opacity-40 group-hover:opacity-100 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" style={{ color: "var(--lime)" }} />
            </a>

            <a
              href={`tel:${BRAND.phoneTel}`}
              data-testid="contact-phone"
              className="flex items-center gap-4 py-5 border-t border-white/10 group"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--lime)" }}>
                <Phone size={18} style={{ color: "var(--ink)" }} />
              </div>
              <div className="flex-1">
                <div className="text-[0.65rem] tracking-[0.25em] uppercase opacity-60">Teléfono</div>
                <div className="font-medium group-hover:text-[var(--lime)] transition-colors" style={{ color: "var(--paper)" }}>
                  {BRAND.phone}
                </div>
              </div>
              <ArrowUpRight size={20} className="opacity-40 group-hover:opacity-100 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" style={{ color: "var(--lime)" }} />
            </a>

            <a
              href={`mailto:${BRAND.email}?subject=Quiero%20destrabar%20mi%20marca`}
              data-testid="contact-cta"
              className="pill pill-lime w-full justify-center mt-8"
            >
              Iniciar conversación <ArrowUpRight size={18} />
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ---------------- FOOTER ---------------- */
const Footer = () => (
  <footer data-testid="footer" className="py-12 border-t border-white/10" style={{ background: "var(--ink-2)" }}>
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row gap-6 items-center justify-between">
      <div className="flex items-center gap-3">
        <img src={BRAND.logoMonoLime} alt="Néctar" className="h-8 w-8" />
        <div>
          <div className="font-display text-lg" style={{ color: "var(--paper)" }}>Néctar Negocios</div>
          <div className="text-[0.6rem] tracking-[0.3em] uppercase opacity-50">Descubrimos el corazón de tu marca</div>
        </div>
      </div>
      <div className="text-xs opacity-50 text-center md:text-right">
        © {new Date().getFullYear()} Néctar Negocios. Todos los derechos reservados.
      </div>
    </div>
  </footer>
);

/* ---------------- APP ---------------- */
function App() {
  useEffect(() => {
    document.title = "Néctar Negocios — Descubrimos el corazón de tu marca";
  }, []);

  return (
    <div className="App grain" style={{ background: "var(--ink)" }}>
      <Navbar />
      <main>
        <Hero />
        <About />
        <Verticales />
        <section id="casos" data-testid="casos-anchor" />
        {VERTICALS.map((v, i) => (
          <VerticalSection key={v.id} v={v} index={i} />
        ))}
        <Clientes />
        <Contacto />
      </main>
      <Footer />
    </div>
  );
}

export default App;
